import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Card, Grid, Skeleton, Stack, Typography } from "@mui/material";
import CalculatorStepper from "../../calculatorStepper";
import { ButtonComponent, FormProviderComponent, SelectionComponent, TextFieldComponent } from "../../../../components/form";
import { useCallback, useEffect, useState } from 'react';
import SnackbarComponent from '../../../../components/snackBar/snackbarComponent';
import { Box } from 'rsuite';

const Label = (label) => {
    return <Typography
        fontWeight={400}
        fontSize={{md: 16, lg: 22}}
        color="black"
    >
        {label}
    </Typography>
}

const CalculatorWebFilter = ({
    defaultData, 
    moveToFormView, 
    buttonLoading,
    dropDownDataLoading,
    tourSitesAndActivities
}) => {
    const [sitesList, setSitesList] = useState([]);
    const [activitiesList, setActivitiesList] = useState([]);

    useEffect(() => {
        if(!tourSitesAndActivities) return;

        console.log(tourSitesAndActivities);

        // Sites
        const siteValuesArray = Object.values(tourSitesAndActivities?.sites);
        const siteKeysArray = Object.keys(tourSitesAndActivities?.sites);

        const sList = [];
        for(let i = 0; i < siteKeysArray.length; i++){
            sList.push({label: siteValuesArray[i], value: siteKeysArray[i]});
        }

        setSitesList(sList);

        // Activities
        const activityValuesArray = Object.values(tourSitesAndActivities?.activities);
        const activityKeysArray = Object.keys(tourSitesAndActivities?.activities);

        const aList = [];
        for(let i = 0; i < activityKeysArray.length; i++){
            aList.push({label: activityValuesArray[i], value: activityKeysArray[i]});
        }

        setActivitiesList(aList);
    }, [tourSitesAndActivities]);

    const formSchema = yup.object().shape({
        sites: yup.array(),
        activities: yup.array(),
        kids: yup.number().typeError('Invalid number of kids count').test(
            'isValidKids', 
            'Invalid number of kids count',
            () => {
                return Number.parseFloat(values.kids) >= 0;
            }
        ),
        adults: yup.number().required('Adults count is required').typeError('Invalid number of adults count').moreThan(0, 'Adults count should be greater than 0')
    });
    
    const defaultValues = {
        sites: [],
        activities: [],
        kids: 0,
        adults: 0
    };
    
    const formMethods = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: defaultValues
    });
    
    const {
        setValue,
        reset: reset,
        handleSubmit: handleSubmit,
        formState: { isSubmitting: isSubmitting },
        watch
    } = formMethods;
    
    const values = watch();

    useEffect(() => {
        setValue('adults', defaultData?.adults);
        setValue('kids', defaultData?.kids);
    }, [defaultData]);

    useEffect(() => {
        if(sitesList.length > 0){
            const selectedSitesList = [];
            for(let i = 0; i < defaultData.sites.length; i++){
                selectedSitesList.push(String(defaultData.sites[i]));
            }
            setValue('sites', selectedSitesList);
        }
    }, [defaultData, sitesList]);

    useEffect(() => {
        if(activitiesList.length > 0){
            const selectedActivitiesList = [];
            for(let i = 0; i < defaultData.activities.length; i++){
                selectedActivitiesList.push(String(defaultData.activities[i]));
            }
            setValue('activities', selectedActivitiesList);
        }
    }, [defaultData, activitiesList]);

    const [snackBar, setSnackBar] = useState({
        message: "",
        open: false,
        color: ""
    });

    const onSubmit = handleSubmit(async(data) => {
        console.log(data);

        if(data.sites.length === 0 && data.activities.length === 0){
            setSnackBar({
                message: "Select at-least either one of site/activity",
                color: "red",
                open: true
            });

            return;
        }

        try{
            moveToFormView(data.sites, data.activities, data.kids, data.adults);
        }
        catch(error){
            setSnackBar({
                message: {error},
                color: "red",
                open: true
            });
        }
    });

    const getStepperFillerCount = useCallback(() => {
        if(values){
            let count = 0;

            if(values.sites?.length > 0 || values.activities?.length){
                count++;
            }

            if(values.adults > 0){
                count++;
            }

            return count;
        }
        
        return 0;
    }, [values]);

    return (
        <>
            <Stack
                direction='column'
                justifyContent='center'
                alignItems='start'
                px={10}
                ml={10}
                pt={7}
                data-aos="fade-right"
            >
                {/* Title */}
                <Typography
                    fontWeight={800}
                    fontSize={50}
                    letterSpacing={0.3}
                    lineHeight={1.25}
                    textAlign='left'
                    color="white"
                    pb={2.5}
                >
                    Plan Your Site Visits and Activity<br />Pricing!
                </Typography>

                {/* Description */}
                <Typography
                    fontWeight={400}
                    fontSize={22}
                    letterSpacing={0.5}
                    lineHeight={1.25}
                    textAlign='left'
                    color='rgba(251, 251, 251, 1)'
                >
                    Instantly calculate total costs by combining site entrance fees, and activity rates in one place.
                </Typography>

                <Card
                    sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        borderRadius: '8px',
                        width: '100%',
                        py: 5,
                        mt: 10
                    }}
                >
                    <Stack 
                        px={10}
                    >
                        {/* Stepper */}
                        <Stack
                            direction='column'
                            justifyContent='center'
                            alignItems='center'
                            pt={2}
                        >
                            <CalculatorStepper 
                                step={getStepperFillerCount()}
                                stepList={[0,1,2]}
                            />
                        </Stack>

                        {/* Form */}
                        <FormProviderComponent methods={formMethods} onSubmit={onSubmit}>
                            <Grid
                                direction='row'
                                justifyContent='space-between'
                                alignItems='start'
                                width='100%'
                                pt={8}
                                container
                                spacing={3}
                            >
                                {/* Sites */}
                                <Grid
                                    size={12}
                                >
                                    {Label('Sites')}

                                    {
                                        dropDownDataLoading
                                        ?
                                        <Skeleton variant="rounded" width='100%' height={55} />
                                        :
                                        <>
                                            {
                                                sitesList.length > 0
                                                ?
                                                <SelectionComponent 
                                                    name="sites"
                                                    label=""
                                                    list={sitesList}
                                                    fullWidth={false}
                                                    multiple
                                                    contrastTheme
                                                    showListIcons
                                                />
                                                :
                                                <Box pb={2}>
                                                    <Typography
                                                        fontWeight={400}
                                                        fontSize={14}
                                                    >
                                                        No sites available
                                                    </Typography>
                                                </Box>
                                            }
                                        </>
                                    }
                                </Grid>

                                {/* Activities */}
                                <Grid
                                    size={12}
                                >
                                    {Label('Activities')}

                                    {
                                        dropDownDataLoading
                                        ?
                                        <Skeleton variant="rounded" width='100%' height={55} />
                                        :
                                        <>
                                            {
                                                activitiesList.length > 0
                                                ?
                                                <SelectionComponent 
                                                    name="activities"
                                                    label=""
                                                    list={activitiesList}
                                                    fullWidth={false}
                                                    multiple
                                                    contrastTheme
                                                    showListIcons
                                                />
                                                :
                                                <Box pb={2}>
                                                    <Typography
                                                        fontWeight={400}
                                                        fontSize={14}
                                                    >
                                                        No activities available
                                                    </Typography>
                                                </Box>
                                            }
                                        </>
                                    }
                                </Grid>

                                {/* Kids */}
                                <Grid
                                    size={6}
                                >
                                    {Label('# Kids (6-11.99)')}

                                    <TextFieldComponent 
                                        name='kids'
                                        label=''
                                        fullWidth
                                        contrastTheme
                                    />
                                </Grid>

                                {/* Adults */}
                                <Grid
                                    size={6}
                                >
                                    {Label('# Adults *')}

                                    <TextFieldComponent 
                                        name='adults'
                                        label=''
                                        fullWidth
                                        contrastTheme
                                    />
                                </Grid>
                            </Grid>

                            {/* Next */}
                            <Stack
                                direction='row'
                                justifyContent='end'
                                alignItems='center'
                                width='100%'
                                py={3}
                            >
                                <ButtonComponent 
                                    label='Next'
                                    type="submit"
                                    inLoading={buttonLoading}
                                    sx={{
                                        width: '150px'
                                    }}
                                />
                            </Stack>
                        </FormProviderComponent>
                    </Stack>
                </Card>
            </Stack>

            <SnackbarComponent 
                message={snackBar.message}
                color={snackBar.color}
                open={snackBar.open}
                handleClose={() => setSnackBar({
                    message: "",
                    open: false
                })}
            />
        </>
    );
}

export default CalculatorWebFilter;